import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../Config/config";
import { UserContext } from "./UserContext";
import { useTenant } from "../Context/TenantContext";
import TenantLoginFooter from "./TenantLoginFooter";
import {
  Eye, EyeOff, GraduationCap, BookOpen, Trophy, Bell, AlertCircle,
} from "lucide-react";

// --- Design Tokens ------------------------------------------------------------
const T = {
  border:      "1px solid #E2E8F0",
  radius:      { sm: "8px", md: "12px", lg: "16px", xl: "20px", xxl: "24px", full: "999px" },
  shadow:      { xs: "0 1px 3px rgba(0,0,0,0.05)", sm: "0 2px 8px rgba(0,0,0,0.07)", lg: "0 8px 40px rgba(79, 70, 229,0.13)" },
  brand:       "var(--primary-blue, #4F46E5)",          // Match Admin Blue
  brandLight:  "rgba(79, 70, 229,0.08)",
  brandBorder: "rgba(79, 70, 229,0.18)",
  text: {
    heading:  "#0F172A",
    body:     "#334155",
    muted:    "#64748B",
    disabled: "#94A3B8",
  },
  bg: { main: "#F8FAFC", section: "#FFFFFF" },
};

// --- Feature pill on the left branding panel ----------------------------------
const FeaturePill = ({ icon: Icon, label }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: "10px",
    padding: "10px 14px", borderRadius: T.radius.lg,
    background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.18)",
    backdropFilter: "blur(6px)",
  }}>
    <div style={{
      width: "28px", height: "28px", borderRadius: T.radius.md,
      background: "rgba(255,255,255,0.15)", flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <Icon style={{ width: "14px", height: "14px", color: "#FFFFFF" }} />
    </div>
    <span style={{ fontSize: "13px", fontWeight: 600, color: "rgba(255,255,255,0.92)" }}>
      {label}
    </span>
  </div>
);

// --- Labelled field wrapper ----------------------------------------------------
const Field = ({ label, id, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
    <label
      htmlFor={id}
      style={{ fontSize: "11px", fontWeight: 700, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}
    >
      {label}
    </label>
    {children}
  </div>
);

const inputBase = {
  width: "100%", padding: "11px 14px", fontSize: "14px", fontWeight: 600,
  color: "#0F172A", background: "#F8FAFC", border: "1.5px solid #E2E8F0",
  borderRadius: "12px", outline: "none", transition: "border-color 0.15s, box-shadow 0.15s",
  boxSizing: "border-box",
};

// --- TenantStudentLogin -------------------------------------------------------
const TenantStudentLogin = () => {
  const navigate = useNavigate();
  const { setUser } = useContext(UserContext);
  const { getCollegeName, getLogoUrl } = useTenant();

  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [password,        setPassword]        = useState("");
  const [showPassword,    setShowPassword]    = useState(false);
  const [error,           setError]           = useState("");
  const [loading,         setLoading]         = useState(false);
  const [focused,         setFocused]         = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/colleges/student-login`, {
        userId: usernameOrEmail,
        password,
      });

      if (res.data?.user) {
        const { sessionToken, user, tenant } = res.data;
        const userWithTenant = { ...user, tenant };
        localStorage.setItem("user", JSON.stringify(userWithTenant));
        localStorage.setItem("session_token", sessionToken);
        setUser(userWithTenant);
        navigate("/dashboard");
      } else {
        setError(res.data?.error || "Login failed. Please try again.");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const collegeName = getCollegeName();
  const logoUrl     = getLogoUrl();

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: T.bg.main, fontFamily: "inherit" }}>

      {/* -- Two-column body --------------------------------------------------- */}
      <main style={{ flex: 1, display: "flex", overflow: "hidden" }}>

        {/* -- Left branding panel (wide screens only) ----------------------- */}
        <div
          className="student-branding-panel"
          style={{
            display: "none",
            flex: "0 0 420px",
            background: "linear-gradient(145deg, var(--secondary-blue, #1E3A8A) 0%, var(--primary-blue, #4F46E5) 55%, #7C3AED 100%)",
            padding: "52px 44px",
            flexDirection: "column",
            justifyContent: "space-between",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative blobs */}
          <div style={{ position: "absolute", top: "-80px", right: "-80px", width: "260px", height: "260px", borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
          <div style={{ position: "absolute", bottom: "-100px", left: "-60px", width: "320px", height: "320px", borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />

          {/* College identity */}
          <div style={{ position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "18px", marginBottom: "32px" }}>
              <div style={{
                width: "72px", height: "72px", borderRadius: T.radius.lg,
                background: "rgba(255,255,255,0.15)", border: "2px solid rgba(255,255,255,0.25)",
                overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
                backdropFilter: "blur(6px)", flexShrink: 0,
              }}>
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={collegeName}
                    style={{ width: "100%", height: "100%", objectFit: "contain", padding: "8px" }}
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <GraduationCap style={{ width: "32px", height: "32px", color: "#FFFFFF" }} />
                )}
              </div>

              <div>
                <h1 style={{
                  fontSize: "24px", fontWeight: 900,
                  color: "#FFFFFF", letterSpacing: "-0.02em", lineHeight: 1.2,
                  margin: 0,
                }}>
                  {collegeName || "Student Portal"}
                </h1>
                <p style={{ fontSize: "14px", color: "rgba(255,255,255,0.72)", fontWeight: 500, margin: "4px 0 0" }}>
                  Your learning journey starts here
                </p>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <FeaturePill icon={BookOpen}      label="Access Course Materials" />
              <FeaturePill icon={Trophy}        label="Track Your Progress" />
              <FeaturePill icon={Bell}          label="Stay Updated" />
            </div>
          </div>

          <p style={{ fontSize: "11px", color: "rgba(255,255,255,0.38)", fontWeight: 500, margin: 0, position: "relative" }}>
            Powered by The Skill Genie
          </p>
        </div>

        {/* -- Right login column -------------------------------------------- */}
        <div style={{
          flex: 1, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          padding: "48px 24px", overflowY: "auto",
        }}>
          <div style={{ width: "100%", maxWidth: "420px" }}>

            {/* Mobile-only college badge */}
            <div className="student-mobile-header" style={{ textAlign: "center", marginBottom: "28px" }}>
              {logoUrl && (
                <div style={{
                  width: "60px", height: "60px", borderRadius: T.radius.xl,
                  background: T.bg.main, border: `2px solid ${T.brandBorder}`,
                  overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 12px", boxShadow: T.shadow.sm,
                }}>
                  <img
                    src={logoUrl}
                    alt={collegeName}
                    style={{ width: "100%", height: "100%", objectFit: "contain", padding: "6px" }}
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                </div>
              )}
              <h2 style={{ fontSize: "18px", fontWeight: 800, color: T.text.heading, margin: "0 0 4px" }}>
                {collegeName || "Student Portal"}
              </h2>
              <p style={{ fontSize: "13px", color: T.text.muted, margin: 0 }}>Your learning journey starts here</p>
            </div>

            {/* Login card */}
            <div style={{
              background: T.bg.section, border: T.border, borderRadius: T.radius.xxl,
              boxShadow: T.shadow.lg, overflow: "hidden",
            }}>
              <div style={{ padding: "32px 32px 36px" }}>
                <div style={{ marginBottom: "28px" }}>
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: "6px",
                    padding: "3px 12px", borderRadius: T.radius.full,
                    background: T.brandLight, border: `1px solid ${T.brandBorder}`,
                    fontSize: "10px", fontWeight: 700, color: T.brand,
                    textTransform: "uppercase", letterSpacing: "0.06em",
                    marginBottom: "12px",
                  }}>
                    <GraduationCap style={{ width: "11px", height: "11px" }} />
                    Student Portal
                  </div>
                  <h2 style={{ fontSize: "22px", fontWeight: 800, color: T.text.heading, margin: "0 0 6px", letterSpacing: "-0.02em" }}>
                    Welcome back
                  </h2>
                  <p style={{ fontSize: "13px", color: T.text.muted, margin: 0, fontWeight: 500 }}>
                    Sign in to access your courses and progress.
                  </p>
                </div>

                {/* Error alert */}
                {error && (
                  <div style={{
                    display: "flex", alignItems: "flex-start", gap: "10px",
                    padding: "12px 14px", borderRadius: T.radius.md,
                    background: "#FEF2F2", border: "1px solid #FECACA",
                    marginBottom: "20px",
                  }}>
                    <AlertCircle style={{ width: "15px", height: "15px", color: "#DC2626", flexShrink: 0, marginTop: "1px" }} />
                    <p style={{ fontSize: "13px", color: "#DC2626", fontWeight: 600, margin: 0 }}>{error}</p>
                  </div>
                )}

                <form onSubmit={handleLogin} noValidate style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

                  <Field label="Username or Email" id="usernameOrEmail">
                    <input
                      id="usernameOrEmail"
                      type="text"
                      value={usernameOrEmail}
                      required
                      autoComplete="username"
                      onChange={(e) => setUsernameOrEmail(e.target.value)}
                      onFocus={() => setFocused("u")}
                      onBlur={() => setFocused(null)}
                      placeholder="Enter your username or email"
                      style={{
                        ...inputBase,
                        borderColor: focused === "u" ? T.brand : "#E2E8F0",
                        boxShadow:   focused === "u" ? `0 0 0 3px ${T.brandLight}` : "none",
                      }}
                    />
                  </Field>

                  <Field label="Password" id="password">
                    <div style={{ position: "relative" }}>
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        required
                        autoComplete="current-password"
                        onChange={(e) => setPassword(e.target.value)}
                        onFocus={() => setFocused("p")}
                        onBlur={() => setFocused(null)}
                        placeholder="Enter your password"
                        style={{
                          ...inputBase,
                          paddingRight: "44px",
                          borderColor: focused === "p" ? T.brand : "#E2E8F0",
                          boxShadow:   focused === "p" ? `0 0 0 3px ${T.brandLight}` : "none",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        style={{
                          position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)",
                          background: "none", border: "none", cursor: "pointer", padding: "4px",
                          color: T.text.muted, display: "flex", alignItems: "center",
                        }}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword
                          ? <EyeOff style={{ width: "16px", height: "16px" }} />
                          : <Eye    style={{ width: "16px", height: "16px" }} />}
                      </button>
                    </div>
                  </Field>

                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "-4px" }}>
                    <span
                      onClick={() => navigate("/forgot-password")}
                      style={{ fontSize: "13px", fontWeight: 700, color: T.brand, cursor: "pointer" }}
                    >
                      Forgot password?
                    </span>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: "100%", padding: "13px 20px",
                      background: loading ? "#93C5FD" : T.brand,
                      color: "#FFFFFF", border: "none", borderRadius: T.radius.md,
                      fontSize: "14px", fontWeight: 800, letterSpacing: "0.04em",
                      textTransform: "uppercase", cursor: loading ? "not-allowed" : "pointer",
                      boxShadow: loading ? "none" : "0 4px 16px rgba(79, 70, 229,0.32)",
                      transition: "all 0.15s", marginTop: "4px",
                    }}
                    onMouseEnter={(e) => { if (!loading) e.currentTarget.style.opacity = "0.9"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                  >
                    {loading ? "Signing in..." : "Sign In"}
                  </button>
                </form>
              </div>
            </div>

            {/* Footer note */}
            <p style={{ textAlign: "center", fontSize: "12px", color: T.text.disabled, marginTop: "20px" }}>
              Wrong institution?{" "}
              <a
                href="https://theskillgenie.com"
                style={{ color: T.text.muted, fontWeight: 600, textDecoration: "none" }}
              >
                Go to The Skill Genie
              </a>
            </p>
          </div>
        </div>
      </main>

      <TenantLoginFooter />

      <style>{`
        @media (min-width: 900px) {
          .student-branding-panel { display: flex !important; }
          .student-mobile-header  { display: none !important; }
        }
        @media (max-width: 899px) {
          .student-mobile-header  { display: block !important; }
        }
      `}</style>
    </div>
  );
};

export default TenantStudentLogin;


